import React, { useState, useEffect } from 'react';
import {
    FaChartLine, FaSpinner, FaExclamationTriangle, FaCheckCircle,
    FaCalendarCheck, FaGift, FaUserClock, FaTimesCircle,
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../../config/api';

// Direction B — per-customer admin insights (LTV, payment failure timeline,
// promo cost-to-date, 3-month forecast, churn signals). Renders below the
// Vocus Identifiers section in CustomerDetailsModal. All computation is
// done server-side by handleGetCustomerInsights in admin-customer-management.

const fmtAud = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n) || 0);
const fmtMonth = (key) => {
    if (!key) return '';
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const RISK_STYLES = {
    low: { bg: 'bg-[#3c3836]', border: 'border-[#458588]', text: 'text-[#b8bb26]', label: 'Low' },
    medium: { bg: 'bg-[#3c3320]', border: 'border-[#d79921]', text: 'text-[#fabd2f]', label: 'Medium' },
    high: { bg: 'bg-[#3c1f1f]', border: 'border-[#cc241d]', text: 'text-[#fb4934]', label: 'High' },
};

const CustomerInsightsSection = ({ customer }) => {
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!customer?.UID && !customer?.email) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.UID]);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('adminToken');
            const r = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: token } : {}),
                },
                body: JSON.stringify({
                    action: 'getCustomerInsights',
                    customerId: customer.UID,
                    customerEmail: customer.email,
                }),
            });
            const data = await r.json();
            if (data.success) {
                setInsights(data.insights);
            } else {
                setError(data.error || 'Failed to load insights');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-[#282828] border border-[#3c3836] rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#928374]">
                    <FaSpinner className="animate-spin" /> Loading customer insights…
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="bg-[#3c1f1f] border border-[#cc241d] rounded-lg p-3 text-sm text-[#fb4934]">
                Couldn't load insights: {error}
            </div>
        );
    }
    if (!insights) return null;

    const risk = RISK_STYLES[insights.churnSignals?.riskLevel || 'low'];

    return (
        <div className="bg-[#282828] border border-[#3c3836] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
                <FaChartLine className="text-[#83a598]" />
                <h3 className="text-lg font-semibold text-[#ebdbb2]">Customer Insights</h3>
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-[#3c3836] border border-[#504945] rounded p-3">
                    <p className="text-[10px] uppercase text-[#928374] font-semibold">Lifetime Value</p>
                    <p className="text-xl font-bold text-[#b8bb26]">{fmtAud(insights.lifetimeValue)}</p>
                    <p className="text-[10px] text-[#928374]">{fmtAud(insights.lifetimeNetExGst)} ex-GST</p>
                </div>
                <div className="bg-[#3c3836] border border-[#504945] rounded p-3">
                    <p className="text-[10px] uppercase text-[#928374] font-semibold">Avg Monthly Spend</p>
                    <p className="text-xl font-bold text-[#ebdbb2]">{fmtAud(insights.averageMonthlySpend)}</p>
                    <p className="text-[10px] text-[#928374]">{insights.paidInvoiceCount} paid invoices</p>
                </div>
                <div className="bg-[#3c3836] border border-[#504945] rounded p-3">
                    <p className="text-[10px] uppercase text-[#928374] font-semibold">Months Active</p>
                    <p className="text-xl font-bold text-[#ebdbb2]">{(insights.monthsActive || 0).toFixed(1)}</p>
                    <p className="text-[10px] text-[#928374]">{insights.daysSinceSignup} days since signup</p>
                </div>
                <div className="bg-[#3c3836] border border-[#504945] rounded p-3">
                    <p className="text-[10px] uppercase text-[#928374] font-semibold">Promo Cost To Date</p>
                    <p className="text-xl font-bold text-[#d3869b]">{fmtAud(insights.promoCostToDate)}</p>
                    <p className="text-[10px] text-[#928374]">{insights.hasPromo ? 'promo active' : 'no active promo'}</p>
                </div>
            </div>

            {/* Churn risk */}
            <div className={`${risk.bg} ${risk.border} border rounded-lg p-3 mb-4`}>
                <div className="flex items-start gap-2">
                    <FaUserClock className={risk.text} />
                    <div className="flex-1">
                        <p className={`text-xs font-bold uppercase ${risk.text}`}>Churn Risk: {risk.label}</p>
                        {insights.churnSignals?.reasons?.length > 0 ? (
                            <ul className="text-xs text-[#bdae93] mt-1 list-disc list-inside space-y-0.5">
                                {insights.churnSignals.reasons.map((r) => <li key={r}>{r}</li>)}
                            </ul>
                        ) : (
                            <p className="text-xs text-[#928374] mt-1">No red flags. Healthy payment history.</p>
                        )}
                        <p className="text-[10px] text-[#928374] mt-1">
                            Last successful payment: {insights.daysSinceLastPayment == null ? 'never' : `${insights.daysSinceLastPayment} days ago`}
                            {' · '}
                            Default PM: {insights.churnSignals?.hasDefaultPM ? '✓' : '✗ missing'}
                        </p>
                    </div>
                </div>
            </div>

            {/* 3-month forecast */}
            <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <FaCalendarCheck className="text-[#fabd2f]" />
                    <p className="text-xs font-bold uppercase text-[#bdae93]">3-Month Forecast</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {(insights.forecast3Month || []).map((f) => (
                        <div key={f.month} className="bg-[#1d2021] rounded p-2 text-center">
                            <p className="text-[10px] text-[#928374]">{fmtMonth(f.month)}</p>
                            <p className={`text-sm font-bold ${f.promoApplied ? 'text-[#d3869b]' : 'text-[#ebdbb2]'}`}>
                                {fmtAud(f.expectedAmount)}
                            </p>
                            {f.promoApplied && (
                                <p className="text-[9px] text-[#d3869b] mt-0.5 inline-flex items-center gap-0.5">
                                    <FaGift size={7} /> promo
                                </p>
                            )}
                        </div>
                    ))}
                </div>
                <p className="text-[10px] text-[#928374] mt-2">
                    Assumes one charge per month at current effective price. Switches to base price after promo end date.
                </p>
            </div>

            {/* Payment failure timeline */}
            {insights.paymentFailures && insights.paymentFailures.length > 0 ? (
                <div className="bg-[#3c1f1f] border border-[#cc241d] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <FaTimesCircle className="text-[#fb4934]" />
                        <p className="text-xs font-bold uppercase text-[#fb4934]">
                            Payment Failures ({insights.failedInvoiceCount} total · {insights.recentFailureCount} in last 90 days)
                        </p>
                    </div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                        {insights.paymentFailures.map((f, idx) => (
                            <div key={`${f.invoiceNumber}-${idx}`} className="flex items-start justify-between gap-3 text-xs py-1 border-b border-[#3c3836] last:border-0">
                                <div className="flex-1 min-w-0">
                                    <p className="text-[#ebdbb2] font-mono">{f.invoiceNumber}</p>
                                    <p className="text-[10px] text-[#928374]">{fmtDate(f.date)}{f.error ? ` — ${f.error}` : ''}</p>
                                </div>
                                <p className="text-[#fb4934] font-medium whitespace-nowrap">{fmtAud(f.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="bg-[#3c3836] border border-[#458588] rounded p-2 text-xs text-[#b8bb26] flex items-center gap-2">
                    <FaCheckCircle /> No failed payments on record.
                </div>
            )}
        </div>
    );
};

export default CustomerInsightsSection;
